import { useEffect, useState } from 'react';
import {
  useCreateFranchiseeNote,
  useDeleteFranchiseeNote,
  useFranchiseeNotesQuery,
  useUpdateFranchiseeNote,
} from '../api/queries';
import { showToast } from './Toast';
import type { FranchiseeNote } from '../types';

type Props = {
  af: string;
  /** Franchisee name for the drawer heading, so a note is never filed blind. */
  label: string;
  open: boolean;
  onClose: () => void;
};

function actorLabel(actor: { name: string | null; email: string }): string {
  return actor.name?.trim() || actor.email;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

/** True when the note has actually been edited since it was written — an edit
 *  by the original author still counts, so the timeline stays honest. */
function wasEdited(note: FranchiseeNote): boolean {
  return note.updatedBy !== null;
}

export default function FranchiseeNotesDrawer({ af, label, open, onClose }: Props) {
  const q = useFranchiseeNotesQuery(af, open);
  const create = useCreateFranchiseeNote(af);
  const update = useUpdateFranchiseeNote(af);
  const remove = useDeleteFranchiseeNote(af);

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  // Drop any half-typed edit when the drawer closes, so reopening is a clean
  // slate rather than a stale form pointing at a note that may have changed.
  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setEditDraft('');
    }
  }, [open]);

  const notes = q.data?.notes ?? [];
  const zohoSync = q.data?.zohoSync;

  async function addNote() {
    const body = draft.trim();
    if (!body) return;
    try {
      await create.mutateAsync(body);
      setDraft('');
      showToast('Note added');
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  async function saveEdit(id: number) {
    const body = editDraft.trim();
    if (!body) return;
    try {
      await update.mutateAsync({ id, body });
      setEditingId(null);
      setEditDraft('');
      showToast('Note updated');
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  async function deleteNote(note: FranchiseeNote) {
    const who = actorLabel(note.author);
    // A note that reached Zoho stays there — Zoho is the legal record and
    // exposes no delete — so say so before the admin confirms, not after.
    const zohoWarning =
      note.zohoNoteId && zohoSync?.deleteCaveat ? `\n\n${zohoSync.deleteCaveat}` : '';
    if (!window.confirm(`Delete this note by ${who}? This can't be undone.${zohoWarning}`)) {
      return;
    }
    try {
      await remove.mutateAsync(note.id);
      if (editingId === note.id) setEditingId(null);
      showToast('Note deleted');
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  return (
    <>
      <div
        className={`call-detail-overlay${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`call-detail-panel notes-panel${open ? ' open' : ''}`}
        role="dialog"
        aria-label={`Internal notes for ${label}`}
        aria-hidden={!open}
      >
        <div className="cdp-header">
          <h3>Notes — {label}</h3>
          <button className="cdp-close" onClick={onClose} aria-label="Close notes">
            ×
          </button>
        </div>

        <div className="cdp-body">
          {/* Standing reminder of who can see this. The button is berry-red and
              admin-gated, but a note is a thing people write candidly, so the
              audience is spelled out rather than implied. */}
          <div className="notes-scope">
            <strong>Internal — admins only.</strong> This franchisee does not see these
            notes on their dashboard.
          </div>

          {zohoSync && !zohoSync.enabled && (
            <div className="notes-zoho-warning">
              <strong>Not syncing to Zoho.</strong> {zohoSync.reason}
            </div>
          )}

          <div className="notes-composer">
            <textarea
              className="notes-textarea"
              placeholder={`Add a note about ${label}…`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
            />
            <div className="notes-composer-actions">
              <button
                className="notes-btn-primary"
                onClick={addNote}
                disabled={!draft.trim() || create.isPending}
              >
                {create.isPending ? 'Adding…' : 'Add note'}
              </button>
            </div>
          </div>

          {q.isLoading && <div className="notes-empty">Loading notes…</div>}
          {q.isError && (
            <div className="notes-error">Couldn't load notes: {(q.error as Error).message}</div>
          )}
          {!q.isLoading && !q.isError && notes.length === 0 && (
            <div className="notes-empty">No notes yet for {label}.</div>
          )}

          <ul className="notes-list">
            {notes.map((note) => (
              <li key={note.id} className="notes-item">
                {editingId === note.id ? (
                  <>
                    <textarea
                      className="notes-textarea"
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={4}
                    />
                    <div className="notes-item-actions">
                      <button
                        className="notes-btn-primary"
                        onClick={() => saveEdit(note.id)}
                        disabled={!editDraft.trim() || update.isPending}
                      >
                        {update.isPending ? 'Saving…' : 'Save'}
                      </button>
                      <button className="notes-btn-link" onClick={() => setEditingId(null)}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="notes-body">{note.body}</div>
                    <div className="notes-meta">
                      <span className="notes-author">{actorLabel(note.author)}</span>
                      <span className="notes-date">{formatWhen(note.createdAt)}</span>
                      {wasEdited(note) && note.updatedBy && (
                        <span className="notes-edited">
                          edited by {actorLabel(note.updatedBy)} · {formatWhen(note.updatedAt)}
                        </span>
                      )}
                      {/* Only shown once a note has actually been through the
                          sync — silence here means "never attempted", which is
                          the normal state on dev and staging. */}
                      {note.zohoSyncError ? (
                        <span className="notes-sync-failed" title={note.zohoSyncError}>
                          Zoho sync failed
                        </span>
                      ) : (
                        note.zohoSyncedAt && <span className="notes-sync-ok">Synced to Zoho</span>
                      )}
                    </div>
                    <div className="notes-item-actions">
                      {/* Any admin may edit any note — the team works accounts
                          together, so notes are shared, not owned. */}
                      <button
                        className="notes-btn-link"
                        onClick={() => {
                          setEditingId(note.id);
                          setEditDraft(note.body);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="notes-btn-link notes-btn-danger"
                        onClick={() => deleteNote(note)}
                        disabled={remove.isPending}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
